import { DOMHelper } from "../Helpers/DOMHelper.js";

export class Modal {
    constructor(app, classAtt, content, openBtnSelector, additionalClasses) {
        this.app = app;
        this.parentId = app.config.elementId;
        this.classAtt = classAtt;
        this.content = content;
        this.openBtnSelector = openBtnSelector;
        this.additionalClasses = additionalClasses || "";
        this.hideCallback = null;
        this.showCallback = null;
        this.isMouseDownOnModal = false;
        this.ariaLabel = null; // dialog name for screen readers; set before init()
        this.returnFocusEl = null;
        this.keydownHandler = (event) => this.onKeydown(event);
    }

    addHtml() {
        // <span class="drs-close-modal-btn">x</span>
        let html =
            `<div class="pm-drs-modal ` +
            this.classAtt +
            " " +
            this.additionalClasses +
            `">
			<!-- <div class="drs-modal-background"></div> --!>
			<div class="drs-modal-content" role="dialog" aria-modal="true" tabindex="-1" aria-label="` + (this.ariaLabel || "Dialog") + `">
                <span class="drs-close-modal-btn" role="button" tabindex="0" aria-label="Close">&times;</span>
				` +
            this.content +
            `
			</div>
		</div>
		`;
        let pmDrsModalsSelector = "." + this.app.wrapperClassId + ".pmdrs-modals";
        document.querySelector(pmDrsModalsSelector).insertAdjacentHTML("beforeend", html);
        this.el = this.getModalElement();
        this.labelFields();
    }

    // Accessibility: every visible field gets a name. The templates put the label text in a
    // sibling <label> or a "...label" div inside the same group, rarely linked to the field
    // (included templates can't carry unique ids: a VLP can render one modal per vehicle).
    labelFields() {
        const modal = this.getModalElement();
        if (!modal) return;
        modal.querySelectorAll("input:not([type=hidden]):not([type=button]):not([type=submit]), select, textarea").forEach((field) => {
            if (field.getAttribute("aria-label") || field.getAttribute("aria-labelledby") || field.closest("label")) return;
            if (field.id && modal.querySelector('label[for="' + CSS.escape(field.id) + '"]')) return;
            const group = field.closest(".contact-form-group, [class$='-label-container'], [class*='-label-container ']");
            if (!group) return;
            const labelEl = [...group.querySelectorAll("label, [class*='label']")].find(
                (el) => !el.contains(field) && !/error/.test(el.className) && el.tagName !== "SELECT" && el.tagName !== "INPUT" && el.textContent.trim()
            );
            if (!labelEl) return;
            if (!labelEl.id) labelEl.id = "pmdrs-label-" + (Modal.labelCount = (Modal.labelCount || 0) + 1);
            field.setAttribute("aria-labelledby", labelEl.id);
        });
    }

    getFocusableElements() {
        const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return [...this.getModalElement().querySelectorAll(selector)].filter(
            (el) => el.getClientRects().length && getComputedStyle(el).visibility !== "hidden"
        );
    }

    // Esc closes, Tab stays inside. Only the top-most open modal reacts (e.g. Learn More
    // opened over the deal page closes alone).
    onKeydown(event) {
        Modal.pruneOpenStack();
        if (Modal.openStack[Modal.openStack.length - 1] !== this) return;
        if (event.key === "Escape") {
            event.preventDefault();
            this.hide();
            return;
        }
        if (event.key !== "Tab") return;
        const focusable = this.getFocusableElements();
        if (!focusable.length) {
            event.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const inside = this.getModalElement().contains(active);
        if (event.shiftKey && (active === first || !inside)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !inside)) {
            event.preventDefault();
            first.focus();
        }
    }

    init() {
        this.addHtml();
        this.addHandlers();
    }

    addHandlers() {
        let thisModal = this;

        DOMHelper.addHandler(this.getSelector(".drs-modal-background"), "click", () => {
            thisModal.hide();
        });

        DOMHelper.addHandler(this.getSelector(".drs-close-modal-btn"), "click", () => {
            document.querySelector("body").style.setProperty("position", "inherit", "important");
            thisModal.hide();
        });

        DOMHelper.addHandler(this.getSelector(".drs-close-modal-btn"), "keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            document.querySelector("body").style.setProperty("position", "inherit", "important");
            thisModal.hide();
        });

        DOMHelper.addHandler(this.app.getSelector(this.openBtnSelector), "click", () => {
            thisModal.show();
        });

        DOMHelper.addHandler(thisModal.getModalElement(), "mousedown", (event) => {
            const modalEl = thisModal.getModalElement();
            const scrollbarWidth = modalEl.offsetWidth - modalEl.clientWidth;
            
            // Check if the click is on the scrollbar
            const isScrollbarClick = event.offsetX >= modalEl.clientWidth && scrollbarWidth > 0;
            
            if (!isScrollbarClick) {
                thisModal.isMouseDownOnModal = event.target === modalEl;
            }            
        });

        DOMHelper.addHandler(thisModal.getModalElement(), "mouseup", (event) => {
            if (thisModal.isMouseDownOnModal && event.target == thisModal.getModalElement()) {
                thisModal.hide();
            }
        });
    }

    getModalElement() {
        return document.querySelector(this.app.getSelector("." + this.classAtt + ".pm-drs-modal"));
    }

    show() {
        document.querySelector("body").style.overflowY = "hidden";
        this.loadDeferredImages();
        this.getModalElement().classList.add("show");
        this.labelFields(); // sections can be rendered after addHtml()
        Modal.pruneOpenStack();
        if (!Modal.openStack.includes(this)) {
            // the opener (e.g. Personalize Deal Now) has often lost focus by now, so fall back to
            // the last control the customer clicked or tapped
            let opener = document.activeElement;
            if (!opener || opener === document.body) {
                opener = Modal.lastPointerTarget && Modal.lastPointerTarget.closest && Modal.lastPointerTarget.closest('button, a[href], input, select, textarea, [tabindex]');
            }
            this.returnFocusEl = opener;
            Modal.openStack.push(this);
            document.addEventListener("keydown", this.keydownHandler);
            this.getModalElement().querySelector(".drs-modal-content").focus({ preventScroll: true });
        }
         if (this.showCallback) {
            this.showCallback();
        }
    }

    // Images set with data-src (e.g. the lead-submission animations, ~1.5 MB each) are only
    // downloaded when their modal is first shown, instead of on page load.
    loadDeferredImages() {
        this.getModalElement().querySelectorAll("img[data-src]").forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
        });
    }

    setShowCallback(callback) {
        this.showCallback = callback;
    }

    hide() {
        document.querySelector("body").style.overflowY = "auto";
        this.getModalElement().classList.remove("show");
        const stackIndex = Modal.openStack.indexOf(this);
        if (stackIndex !== -1) {
            Modal.openStack.splice(stackIndex, 1);
            document.removeEventListener("keydown", this.keydownHandler);
            // back to whatever opened it (the Personalize Deal button), if it's still on the page
            if (this.returnFocusEl && document.contains(this.returnFocusEl) && this.returnFocusEl.focus) {
                this.returnFocusEl.focus({ preventScroll: true });
            }
            this.returnFocusEl = null;
        }
        if (this.hideCallback) {
            this.hideCallback();
        }
    }

    setHideCallback(callback) {
        this.hideCallback = callback;
    }

    getSelector(selector) {
        return ".pmdrs-modals ." + this.classAtt + " " + selector;
    }
}

// open modals, top-most last
Modal.openStack = [];

Modal.lastPointerTarget = null;
document.addEventListener("pointerdown", (event) => { Modal.lastPointerTarget = event.target; }, true);

// ThankYouModal.resetDrsForm() wipes the modals container and rebuilds the deal modal, so an
// open instance can lose its element without hide() running — drop those.
Modal.pruneOpenStack = function () {
    Modal.openStack = Modal.openStack.filter((modal) => {
        if (modal.el && modal.el.isConnected) return true;
        document.removeEventListener("keydown", modal.keydownHandler);
        return false;
    });
};
